import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import { Reveal } from "../lib/motion";
import { api } from "../lib/api";
import { findCategory, findIndustry } from "../data/libraryMeta";

export default function LibraryDetail() {
    const { slug } = useParams();
    const nav = useNavigate();
    const [item, setItem] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setItem(null);
        setNotFound(false);
        api.get(`/library/${slug}`)
            .then((r) => setItem(r.data))
            .catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) {
        return (
            <div className="min-h-screen bg-page text-ink">
                <Nav />
                <main className="mx-auto max-w-[720px] px-5 py-24 text-center">
                    <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.3em] text-coral">
                        // Not found
                    </div>
                    <h1 className="font-display text-4xl font-black text-ink lg:text-5xl">
                        We couldn&apos;t find that resource.
                    </h1>
                    <button
                        onClick={() => nav("/library")}
                        className="mt-8 inline-flex items-center gap-2 border-2 border-ink bg-white px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-ink hover:border-coral hover:text-coral"
                        data-testid="library-detail-back-index"
                    >
                        <ArrowLeft className="h-4 w-4" /> Back to Library
                    </button>
                </main>
                <Footer />
            </div>
        );
    }

    const cat = item ? findCategory(item.category) : null;

    return (
        <div className="min-h-screen bg-page text-ink" data-testid="library-detail-page">
            <Nav />
            <main>
                <section className="border-b border-ink/10 py-16 lg:py-24">
                    <div className="mx-auto max-w-[820px] px-5 lg:px-10">
                        <Link
                            to="/library"
                            className="mb-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-ink/60 hover:text-coral"
                            data-testid="library-detail-back"
                        >
                            <ArrowLeft className="h-4 w-4" /> All resources
                        </Link>
                        {item ? (
                            <Reveal>
                                <div className="mb-6 flex flex-wrap items-center gap-3 text-[11px] font-bold uppercase tracking-[0.3em] text-coral">
                                    <span className="border-2 border-coral px-3 py-1 font-display text-sm">
                                        {cat?.label || item.category}
                                    </span>
                                    <span className="flex items-center gap-2 text-ink/55">
                                        <Clock className="h-3.5 w-3.5" />
                                        Updated {new Date(item.updated_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })}
                                    </span>
                                </div>
                                <h1
                                    className="font-display text-4xl font-black leading-[1.02] tracking-tight text-ink sm:text-5xl lg:text-6xl"
                                    data-testid="library-detail-title"
                                >
                                    {item.title}
                                </h1>
                                <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70 lg:text-lg">
                                    {item.excerpt}
                                </p>
                                <div className="mt-6 flex flex-wrap gap-2">
                                    {item.industries.map((ind) => {
                                        const meta = findIndustry(ind);
                                        if (!meta) return null;
                                        return (
                                            <span
                                                key={ind}
                                                className="border border-ink/20 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-ink/70"
                                            >
                                                <span className="mr-1.5">{meta.emoji}</span>
                                                {meta.label}
                                            </span>
                                        );
                                    })}
                                </div>
                            </Reveal>
                        ) : (
                            <div className="text-sm uppercase tracking-widest text-ink/50">
                                Loading…
                            </div>
                        )}
                    </div>
                </section>

                {item && (
                    <section
                        className="bg-white py-16 lg:py-24"
                        data-testid="library-detail-body"
                    >
                        <div className="mx-auto max-w-[720px] px-5 lg:px-10">
                            {item.body.split("\n\n").map((para, i) => (
                                <p
                                    key={i}
                                    className="mb-6 whitespace-pre-line text-base leading-relaxed text-ink/85 lg:text-lg"
                                >
                                    {para}
                                </p>
                            ))}
                        </div>
                    </section>
                )}
            </main>
            <Footer />
        </div>
    );
}
